// Picks up a hand-off that arrived in the URL instead of through the chat widget's callback.
//
// This is the popup-chat route: the chatbot is opened from a button somewhere on the site, so
// there is no embedded chat to register a supportCallback. DocsBot navigates to the support page
// and the bot's own instructions put what it knows into the query string.
//
// Less complete than the callback route - there is no verbatim transcript here, only what the
// bot chose to pass - so the callback route wins whenever both are available.
//
// Everything read here is attacker-controlled: anyone can hand a visitor a link with these
// parameters on it, and the value ends up in a form submission, in the Kentico admin and possibly
// in an autoresponder or a CRM record. It is sanitised on the way in, and again server-side in
// ChatTranscriptComponent.
/** Query parameters the bot is configured to append. Keep in sync with the bot instructions. */
const PARAM_SUBJECT = 'subject';
const PARAM_DESCRIPTION = 'description';
const PARAM_CONVERSATION_ID = 'conversation_id';
const PARAM_CONVERSATION_URL = 'conversation_url';
const PARAM_WEBSITE_URL = 'website_url';
const HANDOFF_PARAMS = [
    PARAM_SUBJECT,
    PARAM_DESCRIPTION,
    PARAM_CONVERSATION_ID,
    PARAM_CONVERSATION_URL,
    PARAM_WEBSITE_URL,
];
const MAX_TEXT_LENGTH = 4000;
const MAX_IDENTIFIER_LENGTH = 300;
const TAGS = /<[^>]*>/g;
const SPACE_CODE_POINT = 32;
/**
 * Drops control characters while keeping the line breaks and tabs a description may legitimately
 * contain. Written as a loop rather than a regular expression, which would mean embedding control
 * characters in the source.
 */
function stripControlCharacters(value) {
    let result = '';
    for (const character of value) {
        const code = character.codePointAt(0) ?? 0;
        if (code >= SPACE_CODE_POINT || character === '\n' || character === '\t') {
            result += character;
        }
    }
    return result;
}
function read(params, name, maxLength = MAX_TEXT_LENGTH) {
    const raw = params.get(name);
    if (!raw) {
        return undefined;
    }
    // Tags are stripped here rather than trusting every later consumer of the form data to encode.
    const value = stripControlCharacters(raw.replace(TAGS, '')).trim().slice(0, maxLength);
    return value ? value : undefined;
}
/**
 * Builds a conversation from the current URL, or returns null when the URL carries no hand-off.
 * A bare `website_url` is not enough on its own - it says nothing about the issue.
 */
export function readConversationFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const subject = read(params, PARAM_SUBJECT);
    const description = read(params, PARAM_DESCRIPTION);
    const conversationId = read(params, PARAM_CONVERSATION_ID, MAX_IDENTIFIER_LENGTH);
    if (!subject && !description && !conversationId) {
        return null;
    }
    return {
        savedAt: Date.now(),
        conversationId,
        conversationUrl: read(params, PARAM_CONVERSATION_URL, MAX_IDENTIFIER_LENGTH),
        ticketSubject: subject,
        ticketMessage: description,
        websiteUrl: read(params, PARAM_WEBSITE_URL, MAX_IDENTIFIER_LENGTH),
        transcript: '',
        // Recorded in the saved text, so whoever reads the ticket knows this did not come from the
        // chat itself and has not been verified.
        fromUrlParameters: true,
    };
}
/**
 * Takes the hand-off parameters back out of the address bar once they have been stored, so a
 * reload or a shared link does not resurrect an old hand-off. Other parameters are left alone.
 */
export function stripHandoffParams() {
    const url = new URL(window.location.href);
    HANDOFF_PARAMS.forEach((name) => url.searchParams.delete(name));
    window.history.replaceState(window.history.state, '', url.toString());
}
